import { useWalletKit, ChainType } from "@web3jskit/walletkit";
import { useEffect, useState, useCallback, useRef } from "react";
import {
  requestChallenge,
  verifyAuth,
  type VerifyResult,
} from "@/api";
import { useAuthStore } from "@/stores";
import { useCreditsStore } from "@/stores/modules/credits";
import { useDepositBlockchainsStore } from "@/stores/modules/depositBlockchains";
import { clearUserScopedStores } from "@/stores/utils/clearUserScoped";
import { setAuthToken } from "@/api/client";
import {
  WalletError,
  classifyWalletError,
  getEip1193ErrorCode,
  type WalletErrorCode,
} from "@/utils/walletError";
import {
  disconnectWalletKitSession,
  ensureLiveEip1193Provider,
  switchNetworkViaWalletKit,
} from "@/utils/walletProvider";

/**
 * 登录状态枚举
 */
export enum LoginStatus {
  /** 未登录 */
  IDLE = 'idle',
  /** 连接钱包中 */
  CONNECTING = 'connecting',
  /** 签名中 */
  SIGNING = 'signing',
  /** 验证中 */
  VERIFYING = 'verifying',
  /** 登录成功 */
  SUCCESS = 'success',
  /** 登录失败 */
  ERROR = 'error',
}

/**
 * 登录 Hook
 * 提供完整的登录流程：连接钱包 → 获取挑战 → 签名 → 验证
 */
export const useLogin = () => {
  const {
    currentChainType,
    setChainType,
  } = useWalletKit();

  // 登录状态
  const [loginStatus, setLoginStatus] = useState<LoginStatus>(LoginStatus.IDLE);
  // 错误信息
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // 登录结果
  const [loginResult, setLoginResult] = useState<VerifyResult | null>(null);
  // 最近一次失败的钱包错误分类（同步可读，供 caller 映射 i18n 文案）
  const lastErrorCodeRef = useRef<WalletErrorCode | null>(null);

  // 确保使用 EVM 链
  useEffect(() => {
    if (currentChainType !== ChainType.EVM) {
      setChainType(ChainType.EVM);
    }
  }, [currentChainType, setChainType]);

  /**
   * 执行登录流程
   * 
   * 流程：
   * 1. 连接钱包
   * 2. 获取登录挑战
   * 3. 使用钱包签名挑战消息
   * 4. 调用验证接口完成登录
   * 
   * @returns 登录结果，失败时返回 null
   */
  const login = useCallback(async (): Promise<VerifyResult | null> => {
    // 重置状态
    setLoginStatus(LoginStatus.CONNECTING);
    setErrorMessage(null);
    setLoginResult(null);
    lastErrorCodeRef.current = null;

    try {
      // 检查是否为 EVM 链
      if (currentChainType !== ChainType.EVM) {
        throw new WalletError('unknown');
      }

      // Step 1: reuse a live WalletKit session, or reconnect when auth is
      // persisted but the EIP-1193 provider was cleared after refresh.
      console.log('[Login] Step 1: Ensuring live wallet provider...');
      const activeProvider = await ensureLiveEip1193Provider();

      // Authorize the origin before switching chains. Some wallets reject
      // wallet_switchEthereumChain with 4100 until the site is connected.
      const accounts = (await activeProvider.request({
        method: 'eth_requestAccounts',
      })) as string[];

      if (!accounts || accounts.length === 0) {
        throw new WalletError('noProvider');
      }

      const walletAddress = accounts[0] as `0x${string}`;
      console.log('[Login] Wallet connected:', walletAddress);

      // Chain switching is best-effort: authentication only needs an address
      // and one personal_sign, and hosted wallet bridges may not implement
      // wallet_switchEthereumChain at all. The recharge / withdraw flows switch
      // the chain themselves before sending a transaction.
      const chain = useDepositBlockchainsStore.getState().blockchains[0];
      const chainIdNum = chain ? Number(chain.chainId) : Number.NaN;
      if (chain && Number.isFinite(chainIdNum)) {
        console.log("[Login] switchNetwork →", chainIdNum);
        try {
          await switchNetworkViaWalletKit(activeProvider, chainIdNum, {
            chainName: chain.chainName,
            iconUrl: chain.chainLogoUrl,
          });
        } catch (switchError) {
          console.warn("[Login] switchNetwork skipped:", switchError);
        }
      } else {
        console.warn("[Login] switchNetwork skipped: no active blockchain");
      }

      // 第 2 步：获取登录挑战
      console.log('[Login] Step 2: Requesting challenge...');
      setLoginStatus(LoginStatus.SIGNING);
      
      const challengeResult = await requestChallenge({ walletAddress });
      
      if (!challengeResult.ok) {
        throw new Error(challengeResult.error.message || '获取挑战失败');
      }

      const { message } = challengeResult.data;
      console.log('[Login] Challenge received');

      // 第 3 步：使用钱包签名挑战消息
      console.log('[Login] Step 3: Signing message...');
      
      // 使用 personal_sign 进行签名（EIP-191 标准）
      const signature = await activeProvider.request({
        method: 'personal_sign',
        params: [message, walletAddress],
      });

      if (!signature || typeof signature !== "string") {
        throw new WalletError('unknown');
      }

      console.log('[Login] Message signed');

      // 第 4 步：验证签名并完成登录
      console.log('[Login] Step 4: Verifying signature...');
      setLoginStatus(LoginStatus.VERIFYING);

      let verifyResult;
      try {
        verifyResult = await verifyAuth({
          walletAddress,
          signature: signature as string,
        });
      } catch (apiError) {
        // API 调用失败（网络错误或 500 等）
        const errorMessage = apiError instanceof Error ? apiError.message : '服务器错误';
        console.error('[Login] API Error:', errorMessage);
        throw new Error(errorMessage);
      }

      // 检查 API 响应是否成功
      if (!verifyResult || !verifyResult.ok) {
        const errorMsg = verifyResult?.error?.message || '验证失败';
        const errorCode = verifyResult?.error?.code || 'UNKNOWN';
        console.error('[Login] Verification failed:', {
          code: errorCode,
          message: errorMsg,
          response: verifyResult,
        });
        throw new Error(errorMsg);
      }

      console.log('[Login] Verification successful:', {
        userId: verifyResult.data.userId,
        walletAddress: verifyResult.data.walletAddress,
      });

      const { accessToken, userId } = verifyResult.data;

      // 登录新账号前清空上一账号内存态，并删除旧版本遗留的业务缓存快照，
      // 避免进入 Keys / Dashboard 时闪过上一账号数据。
      clearUserScopedStores();

      // 使用 Store 保存登录状态（自动持久化）
      const { setAuth } = useAuthStore.getState();
      setAuth(verifyResult.data);

      // 同步 token 到 API client（确保立即生效）
      setAuthToken(accessToken);

      // credits store 按地址分片读写，需要显式绑定到新账号地址
      useCreditsStore.getState().setCurrentUserAddress(walletAddress);

      console.log('[Login] Login successful! User ID:', userId);

      // 更新状态
      setLoginStatus(LoginStatus.SUCCESS);
      setLoginResult(verifyResult.data);

      return verifyResult.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'login failed';
      // Hosted wallet bridges reject with plain objects, so log the raw value
      // and its parsed EIP-1193 code instead of only the fallback message.
      console.error('[Login] Error:', {
        message: errorMessage,
        eip1193Code: getEip1193ErrorCode(error),
        error,
      });

      // Classify into a wallet error code so the caller can show a precise i18n
      // message (kept in a ref for synchronous read right after `login()`).
      lastErrorCodeRef.current = classifyWalletError(error);

      // 确保登录失败时清除所有认证状态 + 账号相关缓存
      const { logout: storeLogout } = useAuthStore.getState();
      storeLogout();
      clearUserScopedStores();

      setLoginStatus(LoginStatus.ERROR);
      setErrorMessage(errorMessage);
      setLoginResult(null);

      return null;
    }
  }, [currentChainType]);

  /** 读取最近一次登录失败的钱包错误分类（无失败时为 null）。 */
  const getLastWalletErrorCode = useCallback(
    (): WalletErrorCode | null => lastErrorCodeRef.current,
    []
  );

  /**
   * 登出
   */
  const logout = useCallback(() => {
    const { logout: storeLogout } = useAuthStore.getState();
    storeLogout();
    clearUserScopedStores();
    setLoginStatus(LoginStatus.IDLE);
    setLoginResult(null);
    setErrorMessage(null);
  }, []);

  /**
   * Disconnect the current WalletKit session so the next `login()` can
   * reopen the wallet picker instead of reusing the last account.
   */
  const resetWallet = useCallback(async () => {
    await disconnectWalletKitSession();
    setLoginStatus(LoginStatus.IDLE);
    setLoginResult(null);
    setErrorMessage(null);
    lastErrorCodeRef.current = null;
  }, []);

  return {
    login,
    logout,
    resetWallet,
    getLastWalletErrorCode,
    loginStatus,
    errorMessage,
    loginResult,
    isLoading: loginStatus === LoginStatus.CONNECTING || 
               loginStatus === LoginStatus.SIGNING || 
               loginStatus === LoginStatus.VERIFYING,
    isSuccess: loginStatus === LoginStatus.SUCCESS,
    isError: loginStatus === LoginStatus.ERROR,
  };
};
